import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useMemo } from 'react';

// TODO: Merge this logic with what already exists in sidenav.jsx

/** Keep only the menu nodes the current user may see, pruning empty branches. */
const filterSidenavMenus = (sidenavMenus, hasRole, moduleLevel = false) => {
  if (!sidenavMenus) return null;

  return sidenavMenus
    .map((sidenavMenu) => {
      sidenavMenu.children = filterSidenavMenus(sidenavMenu.children, hasRole);

      if (moduleLevel && sidenavMenu.role) {
        // At module level, hide the entire module if the user doesn't have the role
        return hasRole(sidenavMenu.role) ? sidenavMenu : null;
      } else if (sidenavMenu?.children?.length > 0) {
        // Show if it has visible children, regardless of its own role
        return sidenavMenu;
      }
      // If the user has the required role (if any)
      return hasRole(sidenavMenu.role) ? sidenavMenu : null;
    })
    .filter(Boolean);
};

/** Merge two nodes that share the same id. */
const mergeNode = (first = {}, second) => {
  const merged = { ...first, ...second }; // second wins on scalar keys

  // merge children (if either side has them)
  if (first.children || second.children) {
    const combined = [...(first.children || []), ...(second.children || [])];
    const byId = combined.reduce((map, child) => {
      map[child.id] = mergeNode(map[child.id], child);

      return map;
    }, {});
    merged.children = Object.values(byId).sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
  }

  return merged;
};

/**
 * Assembles the module-level side navigation tree for the current org, filtered
 * to the roles the signed-in user holds. This is the single source of truth for
 * "what can this user navigate to" - the SideNav renders it and the AI assistant
 * flattens it into navigation destinations (see {@link flattenSidenavDestinations}).
 */
export const useSidenavMenus = () => {
  const { hasRole, org } = usePrincipal();

  return useMemo(() => {
    // import.meta.glob runs at build time to build the SideNav JSON
    const sidenavContext = import.meta.glob('/src/**/*.sidenav.js', { eager: true });
    let rawSidenavMenus = Object.keys(sidenavContext)
      .filter((key) => {
        if (!key.includes('/org/')) return true;
        // This is the single chokepoint to remove any sideNavs that are org-specific and not part of this org
        return key.includes(`/${org.toLowerCase()}/`);
      })
      .sort((a, b) => {
        // Sort org-specific routes last so they override shared routes during merge
        return a.includes('/org/') - b.includes('/org/');
      })
      .map((key) => sidenavContext[key].default);

    rawSidenavMenus = filterSidenavMenus(rawSidenavMenus, hasRole, true);

    // top-level coalesce
    const byId = rawSidenavMenus.reduce((map, nav) => {
      map[nav.id] = mergeNode(map[nav.id], nav);
      return map;
    }, {});

    return Object.values(byId).sort((a, b) => (a.precedence || 0) - (b.precedence || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);
};

/**
 * Qualifies a leaf's label with its parent section unless the leaf already
 * carries it, so pages that share a generic name (the several "Dashboard" and
 * "Perspective" leaves) stay distinguishable — e.g. the Reconciliation section's
 * "Dashboard" becomes "Reconciliation Dashboard" rather than a bare "Dashboard"
 * the assistant can confuse with an unrelated dashboard.
 */
const qualifyLabel = (section, leaf) => {
  if (!leaf) return section || '';
  if (!section || leaf.toLowerCase().includes(section.toLowerCase())) return leaf;
  return `${section} ${leaf}`;
};

/**
 * Flattens the assembled side navigation tree into its routed destinations
 * (used by the AI assistant and Home widgets): every node that has a route becomes
 * {label, route, icon, module}, with
 * the label qualified by its nearest ancestor section (see {@link qualifyLabel})
 * so the assistant can tell same-named pages apart.
 */
export const flattenSidenavDestinations = (menus) => {
  const destinations = [];
  const walk = (nodes, section, module) => {
    (nodes || []).forEach((node) => {
      const nodeModule = module ?? node;
      if (node.route) {
        destinations.push({
          label: qualifyLabel(section, node.displayName),
          route: node.route,
          icon: node.icon ?? nodeModule.icon,
          module: nodeModule.displayName,
        });
      }
      walk(node.children, node.displayName || section, nodeModule);
    });
  };
  walk(menus, null, null);
  return destinations;
};
